import { type ReactNode, useEffect, useState, useMemo, useCallback } from "react"
import {
  HomeIcon,
  SwatchIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  PlayCircleIcon,
  BellAlertIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import {
  ListBulletIcon,
} from "@heroicons/react/24/outline"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { prefetchAppRoute } from "../../utils/routePrefetch"
import { useStore } from "../../store/useStore"
import { useTheme } from "../../contexts/ThemeContext"
import { useAuthStore } from "../../store/useAuthStore"
import { NotificationCenter } from "../notifications/NotificationCenter"
import { useTranslation } from "react-i18next"
import { Logo } from "../shared/Logo"
import type { User } from "../../types/user"
import { useErrorFeedback } from "../../hooks/useErrorFeedback"
import { ErrorFeedback } from "../shared/ErrorFeedback"
import { motion, useAnimation } from "framer-motion"
import { shellTransition } from "../../components/animations"
import { DarkModeToggle } from "../shared/DarkModeToggle"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Button } from "../ui/button"
import { CaptureComposer } from "../core/CaptureComposer"
import { useCoreTodayStore } from "../../store/useCoreTodayStore"
import { heartbeatService } from "../../services/heartbeatService"

interface MainLayoutProps {
  children: ReactNode
}

type NavigationItem = {
  path: string
  icon: typeof HomeIcon
  label: string
}
type NavigationSection = {
  label: string
  items: NavigationItem[]
}
type CaptureDockItem = {
  action: "capture"
  icon: typeof PlusIcon
  label: string
}

const BREAKPOINT_MOBILE = 800
const BREAKPOINT_DESKTOP = 1280

const withAlpha = (color: string, alpha: string) => {
  const normalized = color.trim()

  if (/^#[\da-f]{3}$/i.test(normalized)) {
    const [, r, g, b] = normalized
    return `#${r}${r}${g}${g}${b}${b}${alpha}`
  }

  if (/^#[\da-f]{6}$/i.test(normalized)) {
    return `${normalized}${alpha}`
  }

  if (/^#[\da-f]{8}$/i.test(normalized)) {
    return `${normalized.slice(0, 7)}${alpha}`
  }

  return normalized
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const {
    user: storeUser,
    isDarkMode,
    setIsDarkMode,
    error: storeError,
    setError: setStoreError,
  } = useStore()
  const { logout } = useAuthStore()
  const { error: errorFeedback, showError, clearError, componentName } = useErrorFeedback()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const logoAnimation = useAnimation()
  const openCapture = useCoreTodayStore((state) => state.openCapture)

  const isCompactLayout = useCallback(() => window.innerWidth < BREAKPOINT_DESKTOP, [])

  const navigationSections = useMemo<NavigationSection[]>(
    () => [
      {
        label: t("navigation.sections.core"),
        items: [
          {
            path: "/today",
            icon: HomeIcon,
            label: t("navigation.today"),
          },
          {
            path: "/plans",
            icon: ListBulletIcon,
            label: t("navigation.plans"),
          },
          {
            path: "/remember",
            icon: SparklesIcon,
            label: t("navigation.remember"),
          },
        ],
      },
      {
        label: t("navigation.sections.support"),
        items: [
          {
            path: "/signals",
            icon: BellAlertIcon,
            label: t("navigation.signals"),
          },
        ],
      },
      {
        label: t("navigation.sections.play"),
        items: [
          {
            path: "/play",
            icon: PlayCircleIcon,
            label: t("navigation.play"),
          },
          {
            path: "/theme",
            icon: SwatchIcon,
            label: t("navigation.theme"),
          },
        ],
      },
    ],
    [t],
  )

  const mobileDockItems = useMemo<Array<NavigationItem | CaptureDockItem>>(
    () =>
      [
        {
          path: "/today",
          icon: HomeIcon,
          label: t("navigation.today"),
        },
        {
          path: "/plans",
          icon: ListBulletIcon,
          label: t("navigation.plans"),
        },
        {
          action: "capture" as const,
          icon: PlusIcon,
          label: t("navigation.capture"),
        },
        {
          path: "/remember",
          icon: SparklesIcon,
          label: t("navigation.remember"),
        },
        {
          path: "/play",
          icon: PlayCircleIcon,
          label: t("navigation.play"),
        },
      ],
    [t],
  )

  const compactMenuSections = useMemo<NavigationSection[]>(
    () =>
      navigationSections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => !mobileDockItems.some((dockItem) => "path" in dockItem && dockItem.path === item.path),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [mobileDockItems, navigationSections],
  )

  const accountNavigationItems = useMemo(
    () => [{ path: "/me", icon: UserCircleIcon, label: t("profile.eyebrow") }],
    [t],
  )

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarOpen((previous) => !previous)
  }, [])

  const handleSidebarClose = useCallback(() => {
    if (isCompactLayout()) {
      setIsSidebarOpen(false)
    }
  }, [isCompactLayout])

  const handleAccountMenuClose = useCallback(() => {
    setIsAccountMenuOpen(false)
    handleSidebarClose()
  }, [handleSidebarClose])

  const handleLogout = useCallback(async () => {
    setIsAccountMenuOpen(false)
    await logout()
    navigate("/login")
  }, [logout, navigate])

  const handleLogoClick = useCallback(async () => {
    await logoAnimation.start({
      rotate: [0, 360],
      transition: { duration: shellTransition.duration, ease: shellTransition.ease },
    })
  }, [logoAnimation])

  const getPageTitle = useCallback(
    (pathname: string, user: User | null) => {
      const titlePrefix = user?.name ? `${user.name}'s` : t("common.your")

      if (pathname === "/today") return `${titlePrefix} ${t("header.today")}`
      if (pathname === "/signals") return `${titlePrefix} ${t("header.signals")}`
      if (pathname === "/plans" || pathname.startsWith("/plans/")) return `${titlePrefix} ${t("header.plans")}`
      if (pathname === "/remember") return `${titlePrefix} ${t("navigation.remember")}`
      if (pathname === "/me") return `${titlePrefix} ${t("profile.eyebrow")}`
      if (pathname === "/play") return `${titlePrefix} ${t("navigation.play")}`

      return `${titlePrefix} ${t("header.planner")}`
    },
    [t],
  )

  const pageTitle = useMemo(
    () => getPageTitle(location.pathname, storeUser),
    [getPageTitle, location.pathname, storeUser],
  )

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
  }, [isDarkMode])

  useEffect(() => {
    const recordPresence = () => {
      if (document.visibilityState !== "visible") return
      void heartbeatService.recordForegroundPresence().catch(() => {
        // Presence is operational context and must never interrupt the app shell.
      })
    }

    recordPresence()
    document.addEventListener("visibilitychange", recordPresence)
    return () => document.removeEventListener("visibilitychange", recordPresence)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (isCompactLayout()) {
        setIsSidebarOpen(false)
      } else {
        setIsSidebarOpen(true)
      }
    }

    handleResize()

    let resizeTimer: NodeJS.Timeout
    const debouncedResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(handleResize, 100)
    }

    window.addEventListener("resize", debouncedResize)
    return () => {
      window.removeEventListener("resize", debouncedResize)
      clearTimeout(resizeTimer)
    }
  }, [isCompactLayout])

  useEffect(() => {
    if (isCompactLayout()) {
      setIsSidebarOpen(false)
    }
    setIsAccountMenuOpen(false)
  }, [isCompactLayout, location.pathname])

  useEffect(() => {
    let scrollTimer: NodeJS.Timeout

    const handleScroll = () => {
      clearTimeout(scrollTimer)
      scrollTimer = setTimeout(() => {
        if (window.innerWidth < BREAKPOINT_MOBILE && isSidebarOpen) {
          setIsSidebarOpen(false)
        }
      }, 150)
    }

    if (window.innerWidth < BREAKPOINT_MOBILE) {
      window.addEventListener("scroll", handleScroll, { passive: true })
    }

    return () => {
      window.removeEventListener("scroll", handleScroll)
      clearTimeout(scrollTimer)
    }
  }, [isSidebarOpen])

  useEffect(() => {
    if (storeUser?.preferences?.theme) {
      const theme = storeUser.preferences.theme
      const shouldBeDark =
        theme === "dark" ||
        (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)

      setIsDarkMode(shouldBeDark)
    }
  }, [setIsDarkMode, storeUser?.preferences?.theme])

  useEffect(() => {
    if (errorFeedback) {
      showError(errorFeedback, "MainLayout")
    }
  }, [errorFeedback, showError])

  const dismissLayoutError = () => {
    clearError()
    setStoreError(null)
  }

  if (errorFeedback || storeError) {
    return (
      <div className="flex min-h-dvh items-center justify-center pt-[max(1rem,env(safe-area-inset-top,0px))] pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
        <div className="w-full max-w-xl space-y-4">
          <ErrorFeedback
            error={errorFeedback || storeError || new Error("Unknown error")}
            componentName={componentName || "MainLayout"}
            isOpen={true}
            onClose={dismissLayoutError}
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={dismissLayoutError}>
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              {t("auth.logout")}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const accountMenuLabel = storeUser?.name || storeUser?.email || t("navigation.profile")
  const isCompactViewport = isCompactLayout()

  const getNavItemStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? withAlpha(colors.primary, "14") : "transparent",
    color: isActive ? colors.primary : colors.text,
    boxShadow: isActive ? `inset 0 0 0 1px ${withAlpha(colors.primary, "20")}` : "none",
  })

  const isNavigationItemActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)

  const renderSidebarContent = () => (
    <div className="space-y-5 px-3 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] pt-3 sm:px-4">
      <Button type="button" onClick={openCapture} className="w-full justify-start rounded-[22px] px-4">
        <PlusIcon className="h-5 w-5" />
        {t("navigation.capture")}
      </Button>
      {navigationSections.map((section) => (
        <section key={section.label} className="space-y-2">
          <p
            className="px-3 text-xs font-semibold uppercase tracking-[0.16em]"
            style={{ color: colors.textSecondary }}
          >
            {section.label}
          </p>
          <div className="space-y-1.5">
            {section.items.map((item) => {
              const isActive = isNavigationItemActive(item.path)

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleSidebarClose}
                  onPointerEnter={() => prefetchAppRoute(item.path)}
                  onFocus={() => prefetchAppRoute(item.path)}
                  className="flex min-h-12 min-w-0 items-center gap-3 rounded-[22px] px-4 py-3 font-medium transition-[transform,background-color,color,box-shadow] duration-200 ease-out hover:-translate-y-px motion-reduce:transform-none"
                  style={getNavItemStyle(isActive)}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )

  const renderMobileDock = () => (
    <nav
      className="fixed inset-x-0 bottom-0 z-20 border-t px-[max(0.5rem,env(safe-area-inset-left,0px))] pb-[env(safe-area-inset-bottom,0px)] pt-1 xl:hidden"
      aria-label={t("navigation.sections.core")}
      style={{
        borderColor: withAlpha(colors.border, "CC"),
        backgroundColor: withAlpha(colors.surface, "F7"),
        boxShadow: `0 -10px 26px ${withAlpha(colors.shadow, "12")}`,
      }}
    >
      <div
        className="mx-auto grid max-w-[31rem] gap-1"
        style={{
          gridTemplateColumns: `repeat(${mobileDockItems.length}, minmax(0, 1fr))`,
        }}
      >
        {mobileDockItems.map((item) => {
          if ("action" in item) {
            return (
              <button
                key={item.action}
                type="button"
                onClick={openCapture}
                className="flex min-h-[3.25rem] min-w-0 flex-col items-center justify-center gap-0.5 rounded-[18px] px-1 text-[0.63rem] font-semibold leading-none text-white transition-[transform,filter] duration-150 active:scale-[0.97] motion-reduce:transform-none"
                style={{ backgroundColor: colors.primary, boxShadow: `0 10px 24px ${withAlpha(colors.primary, "35")}` }}
                aria-label={item.label}
              >
                <item.icon className="h-[1.25rem] w-[1.25rem] shrink-0" />
                <span className="max-w-full truncate">{item.label}</span>
              </button>
            )
          }

          const isActive = isNavigationItemActive(item.path)

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleSidebarClose}
              onPointerEnter={() => prefetchAppRoute(item.path)}
              onFocus={() => prefetchAppRoute(item.path)}
              className="flex min-h-[3.25rem] min-w-0 flex-col items-center justify-center gap-0.5 rounded-[16px] px-1 text-[0.63rem] font-semibold leading-none transition-[transform,background-color,color,box-shadow] duration-200 ease-out active:scale-[0.98] motion-reduce:transform-none"
              style={{
                backgroundColor: isActive ? withAlpha(colors.primary, "14") : "transparent",
                color: isActive ? colors.primary : colors.textSecondary,
                boxShadow: isActive ? `inset 0 0 0 1px ${withAlpha(colors.primary, "20")}` : "none",
              }}
            >
              <item.icon className="h-[1.2rem] w-[1.2rem] shrink-0" />
              <span className="max-w-full truncate">{item.label}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )

  return (
    <div
      className="flex min-h-dvh flex-col bg-background"
      style={{
        backgroundImage: `radial-gradient(circle at top left, ${withAlpha(colors.primary, "08")} 0%, transparent 32%)`,
      }}
    >
      <header
        className="sticky top-0 z-50 border-b bg-background/85 pt-[env(safe-area-inset-top,0px)] backdrop-blur-xl"
        style={{
          borderColor: withAlpha(colors.border, "CC"),
          boxShadow: `0 8px 32px ${withAlpha(colors.shadow, "10")}`,
        }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 py-3 pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] sm:pl-[max(1.5rem,env(safe-area-inset-left,0px))] sm:pr-[max(1.5rem,env(safe-area-inset-right,0px))] lg:pl-[max(2rem,env(safe-area-inset-left,0px))] lg:pr-[max(2rem,env(safe-area-inset-right,0px))]">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {!isCompactViewport ? (
              <Button
                type="button"
                onClick={handleSidebarToggle}
                variant="secondary"
                size="icon"
                className="shrink-0 rounded-2xl"
                aria-label="Toggle sidebar"
              >
                {isSidebarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
              </Button>
            ) : null}

            <motion.button
              type="button"
              animate={logoAnimation}
              onClick={handleLogoClick}
              className="hidden shrink-0 items-center rounded-2xl p-1 sm:flex"
            >
              <Logo size={34} />
            </motion.button>

            <div className="min-w-0 flex-1">
              <p
                className="truncate text-sm font-semibold sm:text-lg"
                style={{ color: colors.text }}
              >
                {pageTitle}
              </p>
              <p className="hidden text-sm sm:block" style={{ color: colors.textSecondary }}>
                {t("header.appName")}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Popover open={isAccountMenuOpen} onOpenChange={setIsAccountMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  className="flex min-h-11 items-center gap-2 rounded-2xl border px-2.5 py-2 transition-[transform,background-color,border-color,color,box-shadow] duration-200 ease-out hover:-translate-y-px motion-reduce:transform-none"
                  style={{
                    borderColor: withAlpha(colors.border, "CC"),
                    background: `linear-gradient(180deg, ${colors.surface} 0%, ${colors.surfaceAlt || colors.surface} 100%)`,
                    color: colors.text,
                    boxShadow: `0 12px 28px ${withAlpha(colors.shadow, "12")}`,
                  }}
                  aria-label={t("navigation.profile")}
                >
                  {storeUser?.photoURL ? (
                    <img
                      src={storeUser.photoURL}
                      alt={accountMenuLabel}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-full"
                      style={{ backgroundColor: withAlpha(colors.primary, "18"), color: colors.primary }}
                    >
                      <UserCircleIcon className="h-5 w-5" />
                    </div>
                  )}
                  <span className="hidden max-w-32 truncate text-sm font-medium lg:block">
                    {accountMenuLabel}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="max-h-[min(42rem,calc(100dvh_-_6rem))] w-[min(21rem,calc(100vw_-_1rem))] overflow-y-auto rounded-3xl border p-3"
                style={{
                  backgroundColor: colors.surface,
                  borderColor: withAlpha(colors.border, "CC"),
                  color: colors.text,
                  boxShadow: `0 24px 64px ${withAlpha(colors.shadow, "18")}`,
                }}
              >
                <div className="space-y-3">
                  <div
                    className="rounded-2xl px-3 py-3"
                    style={{
                      backgroundColor: colors.surfaceAlt || colors.surface,
                      boxShadow: `inset 0 1px 0 ${withAlpha(colors.shadow, "08")}`,
                    }}
                  >
                    <p className="font-medium" style={{ color: colors.text }}>
                      {storeUser?.name || t("navigation.profile")}
                    </p>
                    {storeUser?.email ? (
                      <p className="text-sm" style={{ color: colors.textSecondary }}>
                        {storeUser.email}
                      </p>
                    ) : null}
                  </div>

                  <div
                    className="rounded-2xl border px-3 py-3"
                    style={{
                      borderColor: withAlpha(colors.border, "CC"),
                      backgroundColor: withAlpha(colors.surfaceAlt || colors.surface, "A6"),
                    }}
                  >
                    <div className="mb-2">
                      <p className="text-sm font-medium" style={{ color: colors.text }}>
                        {t("settings.appearance.title")}
                      </p>
                      <p className="text-xs" style={{ color: colors.textSecondary }}>
                        {t("settings.appearance.darkMode")}
                      </p>
                    </div>
                    <DarkModeToggle />
                  </div>

                  {isCompactViewport ? (
                    <div className="space-y-3">
                      {compactMenuSections.map((section) => (
                        <section key={section.label} className="space-y-1.5">
                          <p
                            className="px-3 text-xs font-semibold uppercase tracking-[0.16em]"
                            style={{ color: colors.textSecondary }}
                          >
                            {section.label}
                          </p>
                          <div className="grid grid-cols-1 gap-1">
                            {section.items.map((item) => {
                              const isActive = isNavigationItemActive(item.path)

                              return (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={handleAccountMenuClose}
                                  onPointerEnter={() => prefetchAppRoute(item.path)}
                                  onFocus={() => prefetchAppRoute(item.path)}
                                  className="flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-[transform,background-color,color,box-shadow] duration-200 ease-out hover:-translate-y-px motion-reduce:transform-none"
                                  style={getNavItemStyle(isActive)}
                                >
                                  <item.icon className="h-5 w-5 shrink-0" />
                                  <span className="min-w-0 truncate text-sm font-medium">
                                    {item.label}
                                  </span>
                                </Link>
                              )
                            })}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : null}

                  <div className="space-y-1">
                    {accountNavigationItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleAccountMenuClose}
                        onPointerEnter={() => prefetchAppRoute(item.path)}
                        onFocus={() => prefetchAppRoute(item.path)}
                        className="flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-[transform,background-color,color,box-shadow] duration-200 ease-out hover:-translate-y-px motion-reduce:transform-none"
                        style={getNavItemStyle(location.pathname === item.path)}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </Link>
                    ))}
                  </div>

                  <Button
                    variant="secondary"
                    className="w-full justify-start gap-2 rounded-2xl"
                    onClick={handleLogout}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    {t("auth.logout")}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {!isCompactViewport && isSidebarOpen ? (
          <aside
            className="hidden w-[19rem] shrink-0 border-r xl:block"
            style={{
              borderColor: withAlpha(colors.border, "CC"),
              backgroundColor: withAlpha(colors.surface, "CC"),
            }}
          >
            <div className="sticky top-[var(--app-header-offset)] h-[calc(100dvh-var(--app-header-offset))] overflow-y-auto">
              {renderSidebarContent()}
            </div>
          </aside>
        ) : null}

        <main className="min-w-0 flex-1 overflow-x-clip">
          <div className="mx-auto max-w-7xl pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] pt-5 pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] sm:pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:pt-6 sm:pl-[max(1.5rem,env(safe-area-inset-left,0px))] sm:pr-[max(1.5rem,env(safe-area-inset-right,0px))] lg:pl-[max(2rem,env(safe-area-inset-left,0px))] lg:pr-[max(2rem,env(safe-area-inset-right,0px))] xl:py-6">
            {children}
          </div>
        </main>
      </div>

      <footer
        className="hidden border-t pt-4 xl:block xl:pb-[calc(1rem+env(safe-area-inset-bottom,0px))]"
        style={{
          borderColor: withAlpha(colors.border, "CC"),
          backgroundColor: withAlpha(colors.surface, "F2"),
          color: colors.textSecondary,
        }}
      >
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] text-sm sm:flex-row sm:pl-[max(1.5rem,env(safe-area-inset-left,0px))] sm:pr-[max(1.5rem,env(safe-area-inset-right,0px))] lg:pl-[max(2rem,env(safe-area-inset-left,0px))] lg:pr-[max(2rem,env(safe-area-inset-right,0px))]">
          <div className="flex items-center gap-2">
            <Logo size={22} />
            <span>© {new Date().getFullYear()} Tydy</span>
          </div>
          <a
            href="https://github.com/khareesmith/tydy"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:opacity-80"
            style={{ color: colors.textSecondary }}
          >
            {t("footer.github")}
          </a>
        </div>
      </footer>

      {renderMobileDock()}
      <CaptureComposer />
      <NotificationCenter />
    </div>
  )
}
